// src/components/DocumentRegistryView.tsx
// Formal registry view — table-based with compliance columns.

import { useMemo } from 'react';
import { useI18n } from '@react/hooks';
import {
  Badge,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';

import {
  getEntityId,
  getDocumentStatus,
  getDocumentType,
  getDocumentConfidentiality,
  getDocumentOwner,
  getStatusMapping,
  documentStatusMap,
  documentTypeMap,
  documentConfidentialityMap,
  formatDate,
  canEditDocumentByUser,
} from '@/features/document/logic';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DocumentRegistryViewProps {
  documents: any[];
  onSelect: (id: string) => void;
  onSort?: (field: string) => void;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  currentUserId?: string;
  error?: string | null;
  isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Sort indicator
// ---------------------------------------------------------------------------

function SortIndicator({ field, sortField, sortDirection }: {
  field: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}) {
  if (field !== sortField) {
    return (
      <svg className="ml-1 inline-block h-3 w-3 text-muted-foreground/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }
  const path = sortDirection === 'asc'
    ? 'M5 15l7-7 7 7'
    : 'M19 9l-7 7-7-7';
  return (
    <svg className="ml-1 inline-block h-3 w-3 text-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function RegistrySkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5 animate-pulse">
          <div className="h-4 w-20 rounded bg-muted" />
          <div className="h-4 flex-1 rounded bg-muted" />
          <div className="h-5 w-16 rounded-full bg-muted" />
          <div className="h-5 w-16 rounded-full bg-muted" />
          <div className="h-5 w-16 rounded-full bg-muted" />
          <div className="h-4 w-8 rounded bg-muted" />
          <div className="h-4 w-20 rounded bg-muted" />
          <div className="h-4 w-20 rounded bg-muted" />
          <div className="h-4 w-20 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentRegistryView({
  documents,
  onSelect,
  onSort,
  sortField,
  sortDirection,
  currentUserId,
  error,
  isLoading,
}: DocumentRegistryViewProps) {
  const { t, locale } = useI18n();

  const columns: { key: string; labelKey: string; sortable: boolean; className?: string }[] = useMemo(() => [
    { key: 'number', labelKey: 'document.registry.number', sortable: true, className: 'w-28' },
    { key: 'title', labelKey: 'document.title', sortable: true },
    { key: 'type', labelKey: 'document.docType', sortable: true, className: 'w-28' },
    { key: 'status', labelKey: 'document.status', sortable: true, className: 'w-28' },
    { key: 'confidentiality', labelKey: 'document.confidentiality', sortable: true, className: 'w-28' },
    { key: 'version', labelKey: 'document.registry.version', sortable: true, className: 'w-16' },
    { key: 'owner', labelKey: 'document.owner', sortable: true, className: 'w-28' },
    { key: 'effective_date', labelKey: 'document.registry.effectiveDate', sortable: true, className: 'w-28' },
    { key: 'review_date', labelKey: 'document.registry.reviewDate', sortable: true, className: 'w-28' },
    { key: 'actions', labelKey: 'common.actions', sortable: false, className: 'w-24' },
  ], []);

  if (error) {
    return <ErrorAlert error={error} />;
  }

  if (isLoading) {
    return <RegistrySkeleton />;
  }

  if (documents.length === 0) {
    return (
      <Table>
        <TableBody>
          <TableEmpty colSpan={columns.length}>
            {t('document.registry.empty')}
          </TableEmpty>
        </TableBody>
      </Table>
    );
  }

  const handleHeaderClick = (key: string, sortable: boolean) => {
    if (sortable && onSort) {
      onSort(key);
    }
  };

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={`${col.className || ''} ${col.sortable && onSort ? 'cursor-pointer select-none hover:bg-muted/50' : ''}`}
                onClick={() => handleHeaderClick(col.key, col.sortable)}
              >
                <span className="flex items-center">
                  {t(col.labelKey)}
                  {col.sortable && onSort && (
                    <SortIndicator field={col.key} sortField={sortField} sortDirection={sortDirection} />
                  )}
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((doc: any) => {
            const id = getEntityId(doc);
            const status = getDocumentStatus(doc);
            const docType = getDocumentType(doc);
            const confidentiality = getDocumentConfidentiality(doc);
            const owner = getDocumentOwner(doc);
            const statusInfo = getStatusMapping(documentStatusMap, status);
            const typeInfo = getStatusMapping(documentTypeMap, docType);
            const confInfo = getStatusMapping(documentConfidentialityMap, confidentiality);
            const canEdit = currentUserId ? canEditDocumentByUser(doc, currentUserId) : false;

            return (
              <TableRow
                key={id}
                className="h-11 cursor-pointer transition-colors hover:bg-muted/50"
                onClick={() => onSelect(id)}
              >
                {/* Document Number */}
                <TableCell>
                  <span className="text-xs font-mono text-muted-foreground">
                    {doc.document_number || doc.number || '--'}
                  </span>
                </TableCell>

                {/* Title */}
                <TableCell>
                  <span className="text-sm font-medium text-foreground truncate block">
                    {doc.title || t('common.untitled')}
                  </span>
                </TableCell>

                {/* Type */}
                <TableCell>
                  <Badge variant={typeInfo.variant as any} size="sm">
                    {t(typeInfo.labelKey)}
                  </Badge>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Badge variant={statusInfo.variant as any} size="sm">
                    {t(statusInfo.labelKey)}
                  </Badge>
                </TableCell>

                {/* Confidentiality */}
                <TableCell>
                  <Badge variant={confInfo.variant as any} size="sm">
                    {t(confInfo.labelKey)}
                  </Badge>
                </TableCell>

                {/* Version */}
                <TableCell>
                  <span className="text-xs text-muted-foreground">
                    {doc.version || '1.0'}
                  </span>
                </TableCell>

                {/* Owner */}
                <TableCell>
                  <span className="text-xs text-foreground truncate block">
                    {owner || '--'}
                  </span>
                </TableCell>

                {/* Effective Date */}
                <TableCell>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDate(doc.effective_date, locale)}
                  </span>
                </TableCell>

                {/* Review Date */}
                <TableCell>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDate(doc.review_date, locale)}
                  </span>
                </TableCell>

                {/* Actions */}
                <TableCell>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onSelect(id)}
                      title={t('common.view')}
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </Button>
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onSelect(id)}
                        title={t('common.edit')}
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default DocumentRegistryView;
